import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  CalendarDays,
  Users,
  Clock,
  UserCheck,
  Loader,
  RotateCcw,
  CheckCircle,
  CalendarCheck,
  RefreshCw,
} from "lucide-react";
import { XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line } from "recharts";
import MainLayout from "@/components/layouts";
import { SectionCards } from "@/components/shared/section-cards";
import { AvatarImage } from "@radix-ui/react-avatar";
import { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useState } from "react";
import { $FIXME } from "@/interfaces";
import { useDashboard } from "../dashboard/dashboard.hooks";
import { useIndividualAppointments } from "../appointments/appointments.hooks";
import moment from "moment";
import { Link } from "react-router";

const statusIcons = {
  pending: <Clock className="w-4 h-4" />,
  assigned: <UserCheck className="w-4 h-4" />,
  inprogress: <Loader className="w-4 h-4" />,
  goback: <RotateCcw className="w-4 h-4" />,
  done: <CheckCircle className="w-4 h-4" />,
  completed: <CheckCircle className="w-4 h-4" />,
  rescheduled: <RotateCcw className="w-4 h-4" />,
  inspected: <UserCheck className="w-4 h-4" />,
  cancelled: <Clock className="w-4 h-4" />,
};

const statusColors = {
  pending: "text-yellow-600 bg-yellow-100",
  assigned: "text-blue-600 bg-blue-100",
  inprogress: "text-purple-600 bg-purple-100",
  goback: "text-orange-600 bg-orange-100",
  done: "text-green-600 bg-green-100",
  completed: "text-emerald-600 bg-emerald-100",
  rescheduled: "text-orange-600 bg-orange-100",
  inspected: "text-blue-600 bg-blue-100",
  cancelled: "text-red-600 bg-red-100",
};

export default function IndividualDashboard() {
  const { data, loading, error, refreshData, refreshStats } = useDashboard();
  const { list: appointments } = useIndividualAppointments();
  const [selectedTimeRange, setSelectedTimeRange] = useState("7d");

  const stats = data
    ? [
        {
          name: "Total Appointments",
          value: data.stats.totalAppointments.toString(),
          icon: <CalendarDays size={32} strokeWidth={1} />,
          color: "text-blue-600",
        },
        {
          name: "New Appointments",
          value: data.stats.newAppointments.toString(),
          icon: <CalendarCheck size={32} strokeWidth={1} />,
          color: "text-green-600",
        },
        {
          name: "Completed",
          value: data.stats.completedAppointments.toString(),
          icon: <CheckCircle size={32} strokeWidth={1} />,
          color: "text-emerald-600",
        },
      ]
    : [];

  const upcomingAppointments = data?.upcomingAppointments || [];
  const completedAppointments = data?.completedAppointments || [];

  if (loading && !data) {
    return (
      <MainLayout>
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold tracking-tight">Dashboard</h1>
              <p className="text-muted-foreground">Welcome back! Here's what's happening today.</p>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <Card key={i}>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Loading...</CardTitle>
                  <div className="h-8 w-8 animate-pulse bg-gray-200 rounded" />
                </CardHeader>
                <CardContent>
                  <div className="h-8 w-16 animate-pulse bg-gray-200 rounded" />
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </MainLayout>
    );
  }

  if (error) {
    return (
      <MainLayout>
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold tracking-tight">Dashboard</h1>
              <p className="text-muted-foreground">Welcome back! Here's what's happening today.</p>
            </div>
            <Button onClick={refreshData} variant="outline">
              <RefreshCw className="w-4 h-4 mr-2" />
              Retry
            </Button>
          </div>

          <Card className="border-red-200 bg-red-50">
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-red-600 mb-4">Failed to load dashboard data: {error}</p>
                <Button onClick={refreshData}>Try Again</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Dashboard</h1>
            <p className="text-muted-foreground">Welcome back! Here's what's happening today.</p>
          </div>
          <div className="flex items-center gap-2">
            <Select value={selectedTimeRange} onValueChange={setSelectedTimeRange}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Select time range" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7d">Last 7 days</SelectItem>
                <SelectItem value="30d">Last 30 days</SelectItem>
                <SelectItem value="90d">Last 90 days</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={refreshStats} variant="outline" size="sm">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {stats.map((stat, index) => (
            <Card key={index}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">{stat.name}</CardTitle>
                <div className={stat.color}>{stat.icon}</div>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stat.value}</div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Appointments Tabs */}
        <Tabs defaultValue="upcoming" className="space-y-4">
          <TabsList>
            <TabsTrigger value="upcoming">Upcoming Appointments</TabsTrigger>
            <TabsTrigger value="completed">Recently Completed</TabsTrigger>
          </TabsList>

          <TabsContent value="upcoming" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Upcoming Appointments</CardTitle>
              </CardHeader>
              <CardContent>
                {upcomingAppointments.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">No upcoming appointments</div>
                ) : (
                  <div className="space-y-4">
                    {upcomingAppointments.map((appointment) => (
                      <div key={appointment.id} className="flex items-center justify-between p-4 border rounded-lg">
                        <div className="flex items-center space-x-4">
                          <div className="flex items-center space-x-2">
                            {statusIcons[appointment.status as keyof typeof statusIcons]}
                            <span
                              className={`px-2 py-1 rounded-full text-xs font-medium ${
                                statusColors[appointment.status as keyof typeof statusColors]
                              }`}
                            >
                              {appointment.status.replace("_", " ").toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <p className="font-medium">{appointment.property.name}</p>
                            <p className="text-sm text-muted-foreground">
                              Unit {appointment.property.unit_number} • {appointment.service.name}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-medium">{moment(appointment.date).format("MM/DD/YYYY")}</p>
                          <p className="text-sm text-muted-foreground">{moment(appointment.date).format("h:mm A")}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="completed" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Recently Completed</CardTitle>
              </CardHeader>
              <CardContent>
                {completedAppointments.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">No completed appointments</div>
                ) : (
                  <div className="space-y-4">
                    {completedAppointments.map((appointment) => (
                      <div key={appointment.id} className="flex items-center justify-between p-4 border rounded-lg">
                        <div className="flex items-center space-x-4">
                          <div className="flex items-center space-x-2">
                            {statusIcons[appointment.status as keyof typeof statusIcons]}
                            <span
                              className={`px-2 py-1 rounded-full text-xs font-medium ${
                                statusColors[appointment.status as keyof typeof statusColors]
                              }`}
                            >
                              {appointment.status.replace("_", " ").toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <p className="font-medium">{appointment.property.name}</p>
                            <p className="text-sm text-muted-foreground">
                              Unit {appointment.property.unit_number} • {appointment.service.name}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-medium">{moment(appointment.date).format("MM/DD/YYYY")}</p>
                          <p className="text-sm text-muted-foreground">
                            Completed {moment(appointment.created_at).fromNow()}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Quick Actions */}
        <Card>
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-4">
              <Button asChild>
                <Link to="/appointments">View All Appointments</Link>
              </Button>
              <Button asChild variant="outline">
                <Link to="/schedule">Manage Schedule</Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </MainLayout>
  );
}
